import type { SelectedSystemType } from 'data/types';
import {
  ductedCoverageBasisNote,
  ductedPercentLabel,
  getDuctedCoverage
} from 'utils/ducted-coverage';
import {
  buildDuctedZonePerformance,
  formatRequiredCapacity
} from 'utils/ducted-zone-performance';
import type { ExistingEquipmentSizeType } from 'utils/existing-equipment-size';
import { MATCH_TEXT_CLASS } from 'utils/zone-match-styles';
import DuctedSelectedUnitLines from './ducted-selected-unit-lines';

type DuctedZonePerformanceSectionProps = Readonly<{
  system: SelectedSystemType;
  existingEquipmentSize?: ExistingEquipmentSizeType | null;
}>;

export default function DuctedZonePerformanceSection({
  system,
  existingEquipmentSize
}: DuctedZonePerformanceSectionProps) {
  const coverage = getDuctedCoverage(system);
  const rows = buildDuctedZonePerformance(
    system.configuration.condensers,
    coverage,
    existingEquipmentSize
  );

  if (rows.length === 0) return null;

  const heatingCoverage = coverage
    ? ductedPercentLabel(coverage.heatingCoveragePercent)
    : '—';
  const coolingCoverage = coverage
    ? ductedPercentLabel(coverage.coolingCoveragePercent)
    : '—';
  // An estimated requirement says so under the table, the way the coverage
  // chips' tooltips do: a "Perfect" match against a load derived from the
  // selected tonnage must not read as a Manual J result.
  const basisNote = ductedCoverageBasisNote(coverage?.basis);

  return (
    <div>
      <div className='mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500'>
        Zone Performance &amp; Size Match
      </div>
      <div className='overflow-x-auto rounded-lg border border-gray-200'>
        <table className='w-full text-sm'>
          <thead className='border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500'>
            <tr>
              <th className='px-3 py-2 text-left'>Zone</th>
              <th className='px-3 py-2 text-left'>
                Required Capacity
                <div className='font-normal normal-case tracking-normal text-gray-400'>
                  (Heating / Cooling)
                </div>
              </th>
              <th className='px-3 py-2 text-left'>
                Selected Unit
                <div className='font-normal normal-case tracking-normal text-gray-400'>
                  (Output)
                </div>
              </th>
              <th className='px-3 py-2 text-left'>Size Match</th>
              <th className='px-3 py-2 text-right'>Heating Coverage</th>
              <th className='px-3 py-2 text-right'>Cooling Coverage</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-100'>
            {rows.map((row) => (
              <tr key={row.zone.uniqueId}>
                <td className='whitespace-nowrap px-3 py-2 font-medium text-gray-900'>
                  {row.zone.name}
                </td>
                <td className='px-3 py-2 text-gray-700'>
                  {formatRequiredCapacity(
                    row.totalHeatingRequired,
                    row.totalCoolingRequired
                  )}
                </td>
                <td className='px-3 py-2 text-gray-700'>
                  <DuctedSelectedUnitLines zone={row.zone} />
                </td>
                <td className='px-3 py-2'>
                  <span
                    className={`font-medium ${
                      MATCH_TEXT_CLASS[row.matchVariant]
                    }`}
                  >
                    {row.matchLabel}
                  </span>
                </td>
                <td className='px-3 py-2 text-right'>
                  <span
                    className={`font-medium ${
                      heatingCoverage === '—'
                        ? 'text-gray-400'
                        : 'text-emerald-700'
                    }`}
                  >
                    {heatingCoverage}
                  </span>
                </td>
                <td className='px-3 py-2 text-right'>
                  <span
                    className={`font-medium ${
                      coolingCoverage === '—'
                        ? 'text-gray-400'
                        : 'text-emerald-700'
                    }`}
                  >
                    {coolingCoverage}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className='mt-2 text-xs text-gray-400'>
        Coverage percentages compare the system&apos;s rated capacity to your
        home&apos;s design heating and cooling loads, capped at 100%.
        {basisNote ? ` ${basisNote}` : ''} &quot;—&quot; means the coverage
        couldn&apos;t be calculated.
      </p>
    </div>
  );
}
